import React from 'react';
import { 
  TableHead, 
  TableRow, 
  TableCell, 
  Box,
  Tooltip,
  IconButton
} from '@mui/material';
import { 
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  UnfoldMore as UnfoldMoreIcon
} from '@mui/icons-material';

/**
 * SortableTableHead component for displaying sortable table headers
 * @param {Object} props - Component props
 * @param {Array} props.columns - Array of column definitions
 * @param {Object} props.sortConfig - Current sort state { field, direction }
 * @param {Function} props.handleSortRequest - Handler for sort requests
 * @param {React.ReactNode} [props.leftComponent] - Optional leading header cell content
 */
const SortableTableHead = ({ 
  columns = [], 
  sortConfig = { field: '', direction: 'asc' }, 
  handleSortRequest = () => {}, 
  leftComponent 
}) => {
  /**
   * Renders the appropriate sort icon based on field and current sort state
   * @param {string} field - Field name
   * @returns {JSX.Element} - Sort icon component
   */
  const renderSortIcon = (field) => {
    // Special case for createdAt (Created column) - only show up/down arrows
    if (field === 'createdAt') {
      if (sortConfig.field !== field) {
        return <ArrowDownwardIcon fontSize="small" sx={{ opacity: 0.5 }} />;
      }
      return sortConfig.direction === 'asc' 
        ? <ArrowUpwardIcon fontSize="small" color="primary" />
        : <ArrowDownwardIcon fontSize="small" color="primary" />;
    }

    // For other columns, show three-state icons
    if (sortConfig.field !== field) {
      return <UnfoldMoreIcon fontSize="small" sx={{ opacity: 0.5 }} />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUpwardIcon fontSize="small" color="primary" />
      : <ArrowDownwardIcon fontSize="small" color="primary" />;
  };

  /**
   * Gets tooltip text based on field and current sort state
   * @param {string} field - Field name
   * @returns {string} - Tooltip text
   */
  const getSortTooltip = (field) => {
    // Special case for createdAt (Created column)
    if (field === 'createdAt') {
      if (sortConfig.field !== field) {
        return 'Sort by creation date (newest first)';
      }
      return sortConfig.direction === 'asc' 
        ? 'Sorted by creation date (oldest first)' 
        : 'Sorted by creation date (newest first)';
    }

    // For other columns
    if (sortConfig.field !== field) {
      return `Sort by ${field}`;
    }
    return sortConfig.direction === 'asc' 
      ? `Sorted by ${field} (A to Z)` 
      : `Sorted by ${field} (Z to A)`;
  };

  return (
    <TableHead>
      <TableRow>
        {leftComponent && (
          <TableCell
            key="_left"
            align={'left'}
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              padding: '12px 16px',
              fontWeight: 600
            }}
          >
            {leftComponent}
          </TableCell>
        )}
        {Array.isArray(columns) && columns.filter(Boolean).map((column) => (
          <TableCell
            key={column.field}
            align={column.align || 'left'}
            style={{ 
              minWidth: column.minWidth,
              width: column.width,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              padding: '12px 16px',
              fontWeight: 600
            }}
            data-testid={`table-header-${column.field}`}
          >
            {column.sortable ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  fontWeight: sortConfig.field === column.field ? 'bold' : 'inherit',
                  color: sortConfig.field === column.field ? 'primary.main' : 'inherit',
                  whiteSpace: 'nowrap'
                }}
                onClick={() => handleSortRequest(column.field)}
              >
                {column.headerName}
                <Tooltip title={getSortTooltip(column.field)}>
                  <IconButton 
                    size="small" 
                    sx={{ ml: 0.5 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSortRequest(column.field);
                    }}
                  >
                    {renderSortIcon(column.field)}
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              column.headerName
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

export default SortableTableHead; 